import { useState, useEffect } from 'react';
import { CreditCard, Coins, Calendar, ArrowRight, Check, AlertCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RedeemCommissions = ({ token, onSuccess }) => {
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [monthsToRedeem, setMonthsToRedeem] = useState(1);

  useEffect(() => {
    fetchEligibility();
  }, []);

  const fetchEligibility = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/payments/redeem-eligibility`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEligibility(response.data);
    } catch (error) {
      console.error('Error fetching eligibility:', error);
      toast.error('Error al verificar elegibilidad');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!eligibility?.can_redeem) return;
    
    setRedeeming(true);
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/redeem-subscription`,
        { months: monthsToRedeem },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      if (onSuccess) onSuccess();
      fetchEligibility(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al canjear');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-20 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  const totalCost = eligibility?.subscription_price * monthsToRedeem;
  const canAfford = eligibility?.total_available >= totalCost;

  return (
    <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-2xl p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center">
          <Sparkles className="w-6 h-6 text-violet-600" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">Canjear Comisiones</h3>
          <p className="text-sm text-gray-600">Usa tus ganancias para pagar tu suscripción</p>
        </div>
      </div>

      {/* Balance Display */}
      <div className="bg-white rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 mb-1">Saldo disponible</p>
            <p className="text-3xl font-bold text-violet-600">
              ${eligibility?.total_available?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} <span className="text-lg text-gray-400">MXN</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500 mb-1">Precio mensual</p>
            <p className="text-xl font-semibold text-gray-700">
              ${eligibility?.subscription_price?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
            </p>
          </div>
        </div>
        
        {eligibility?.months_available > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-sm text-green-600 flex items-center gap-1">
              <Check className="w-4 h-4" />
              Puedes canjear hasta <strong>{eligibility.months_available} meses</strong>
            </p>
          </div>
        )}
      </div>

      {eligibility?.can_redeem ? (
        <>
          {/* Month Selector */}
          <div className="mb-6">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              ¿Cuántos meses deseas canjear?
            </label>
            <div className="flex gap-2 flex-wrap">
              {[1, 2, 3, 6, 12].map(months => {
                const cost = eligibility.subscription_price * months;
                const affordable = eligibility.total_available >= cost;
                return (
                  <button
                    key={months}
                    onClick={() => affordable && setMonthsToRedeem(months)}
                    disabled={!affordable}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      monthsToRedeem === months
                        ? 'bg-violet-600 text-white'
                        : affordable
                        ? 'bg-white text-gray-700 hover:bg-violet-50 border border-gray-200'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {months} {months === 1 ? 'mes' : 'meses'}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-white rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Precio por {monthsToRedeem} {monthsToRedeem === 1 ? 'mes' : 'meses'}</span>
              <span>${totalCost?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</span>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
              <span>Saldo disponible</span>
              <span>${eligibility?.total_available?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</span>
            </div>
            <div className="flex items-center justify-between font-semibold pt-2 border-t border-gray-100">
              <span>Saldo restante después del canje</span>
              <span className={canAfford ? 'text-green-600' : 'text-red-600'}>
                ${(eligibility?.total_available - totalCost)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
              </span>
            </div>
          </div>

          {/* Action Button */}
          <Button
            onClick={handleRedeem}
            disabled={redeeming || !canAfford}
            className="w-full bg-violet-600 hover:bg-violet-700 text-white py-6 text-lg"
          >
            {redeeming ? (
              <span className="flex items-center gap-2">
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                Procesando...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Coins className="w-5 h-5" />
                Canjear {monthsToRedeem} {monthsToRedeem === 1 ? 'mes' : 'meses'} por ${totalCost?.toLocaleString('es-MX')} MXN
              </span>
            )}
          </Button>

          <p className="text-xs text-gray-500 text-center mt-3">
            El canje es inmediato. Tu suscripción se extenderá automáticamente.
          </p>
        </>
      ) : (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
          <AlertCircle className="w-8 h-8 text-amber-500 mx-auto mb-2" />
          <p className="text-amber-800 font-medium mb-1">Saldo insuficiente</p>
          <p className="text-sm text-amber-600">
            Necesitas al menos ${eligibility?.subscription_price?.toLocaleString('es-MX')} MXN para canjear un mes de suscripción.
          </p>
          <p className="text-xs text-amber-500 mt-2">
            Te faltan ${(eligibility?.subscription_price - eligibility?.total_available)?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN
          </p>
        </div>
      )}

      {/* Current Membership Info */}
      {eligibility?.membership_expiry && (
        <div className="mt-4 pt-4 border-t border-violet-100">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Calendar className="w-4 h-4" />
            <span>Tu suscripción actual vence: </span>
            <strong className="text-gray-900">
              {new Date(eligibility.membership_expiry).toLocaleDateString('es-MX', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </strong>
          </div>
        </div>
      )}
    </div>
  );
};

export default RedeemCommissions;
